/**
 * Attributes the styled markdown components forward onto the element they
 * render.
 *
 * The component maps replace most elements with styled versions that take only
 * `children`, so an attribute the sanitizer allowed (`<p align="center">`,
 * `<td colspan="2">`, `<ol start="5">`) would be dropped one layer *after* the
 * allowlist and never reach the page. These are the ones that carry meaning
 * rather than decoration; `className` is deliberately absent, because the
 * component owns its styling.
 *
 * `style` is here too: sanitizing has already narrowed it to a few
 * presentational declarations (see `inline-html.ts`), and GFM's own column
 * alignment arrives as `text-align` on `th`/`td`.
 *
 * The `data-source-*` pair is not authored content: `rehypeSourceLines` adds it
 * when a caller asks for source-line tracking, and it has to survive the same
 * swap for a rendered block to still know which source line it came from.
 */
const FORWARDED_ATTRIBUTES = [
  'align',
  'aria-hidden',
  'aria-label',
  'colSpan',
  'data-source-line',
  'data-source-end-line',
  'dir',
  'id',
  'lang',
  'role',
  'rowSpan',
  'start',
  'style',
  'title',
  'type',
] as const;

/**
 * Pick the forwardable attributes out of a markdown component's props, so a
 * styled override can spread them onto the element it renders:
 *
 * ```tsx
 * td: (props) => (
 *   <td {...forwardedHtmlProps(props)} className="px-4 py-3">{props.children}</td>
 * )
 * ```
 */
export function forwardedHtmlProps<P extends object>(props: P): Partial<P> {
  // The prop types react-markdown hands each component are per-element, and
  // some of these attributes are only valid on a few of them, so the lookup is
  // done on an untyped view and handed back as a subset of the caller's props.
  const source = props as Record<string, unknown>;
  const forwarded: Record<string, unknown> = {};
  for (const name of FORWARDED_ATTRIBUTES) {
    if (source[name] !== undefined) forwarded[name] = source[name];
  }
  return forwarded as Partial<P>;
}

/**
 * Tailwind's `list-decimal` / `list-disc` set `list-style-type`, and a class
 * beats the HTML `type` attribute in the cascade, so `<ol type="a">` would
 * still render "1." after the attribute was forwarded. Map the attribute to
 * the equivalent class instead, falling back to the list's default marker.
 */
const LIST_STYLE_CLASSES: Record<string, string> = {
  '1': 'list-decimal',
  a: 'list-[lower-alpha]',
  A: 'list-[upper-alpha]',
  i: 'list-[lower-roman]',
  I: 'list-[upper-roman]',
  circle: 'list-[circle]',
  disc: 'list-disc',
  square: 'list-[square]',
};

/** The marker class for a list, honoring its `type` attribute when it has one. */
export function listStyleClass(props: object, fallback: string): string {
  // `type` is only on the `ol` prop type, but raw HTML can put it on a `ul`.
  const type = (props as Record<string, unknown>).type;
  if (typeof type !== 'string') return fallback;
  return LIST_STYLE_CLASSES[type] ?? fallback;
}
